import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useConfirm } from "../lib/confirm";
import { Plus, Edit2, Trash2, X, Search, Globe2, MapPin, Mail, Phone } from "lucide-react";

const emptyForm = {
  name: "", number: "", obedience: "", city: "", country: "", jurisdiction: "",
  year_opened: "", founded: "", address: "", email: "", phone: "", website: "",
  // Legacy lodge-officer fields preserved for backward compatibility with
  // existing rows. UI no longer surfaces them — see Grand-Lodge officers below.
  venerabil_maestru: "", prim_supraveghetor: "", secretar: "",
  // Grand-Lodge officers exposed in the form (12-step canonical hierarchy).
  mare_maestru: "", deputy_grand_master: "", mare_secretar: "",
  senior_grand_warden: "", junior_grand_warden: "",
  grand_treasurer: "", grand_orator: "", grand_chancellor: "",
  grand_expert: "", grand_director_of_ceremonies: "",
  grand_almoner: "", grand_inner_guard: "",
  meeting: "",
  is_private: false, notes: "",
};

export default function RecognizedLodgesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Child-lodge admins (sub-lodges) see the form/list as a generic "Recognised
  // Lodges" registry with regular officers (Worshipful Master, Senior /
  // Junior Warden, Secretary). Obedience admins (top-level Grand Lodge)
  // see "Recognised Grand Lodges" with Grand-officer labels.
  const isGrandLodgeContext = !user?.child_lodge_id;
  // Tiny helper: in the Grand-Lodge context we prefer the `recognized.*`
  // Grand-officer keys; in the child-lodge context we fall back to the
  // generic lodge-officer keys already used elsewhere in the app.
  const tx = (grandKey, lodgeKey, fallback) =>
    isGrandLodgeContext ? t(grandKey, fallback) : t(lodgeKey, fallback);

  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/lodges");
      setItems(data);
    } catch (e) { setError(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return items;
    return items.filter((l) =>
      (l.name || "").toLowerCase().includes(q) ||
      (l.obedience || "").toLowerCase().includes(q) ||
      (l.city || "").toLowerCase().includes(q) ||
      (l.country || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (l) => { setEditing(l.id); setForm({ ...emptyForm, ...l }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      if (editing) await api.put(`/lodges/${editing}`, form);
      else await api.post("/lodges", form);
      setShowForm(false);
      toast.success(editing ? t("recognized.updatedSuccess") : t("recognized.createdSuccess"));
      await load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const remove = async (l) => {
    const ok = await confirm({ title: t("common.delete"), message: t("recognized.deleteConfirm") });
    if (!ok) return;
    try {
      await api.delete(`/lodges/${l.id}`);
      toast.success(t("recognized.deletedSuccess"));
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="recognized-lodges-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="page-title">{tx("recognized.title", "recognized.titleChild", "Recognised Lodges")}</h1>
          <p className="page-subtitle">{filtered.length}</p>
        </div>
        {isAdmin && (
          <button className="btn-primary-gold flex items-center gap-2" onClick={openCreate} data-testid="add-lodge-btn">
            <Plus size={14} /> {tx("recognized.newLodge", "recognized.newLodgeChild", "NEW LODGE")}
          </button>
        )}
      </div>

      <div className="gold-divider my-8" />

      {error && <div className="error-banner mb-4">{error}</div>}

      <div className="relative mb-5 max-w-xl">
        <Search size={16} style={{ position: "absolute", left: 14, top: 14, color: "#858481" }} />
        <input className="input-lodge" style={{ paddingLeft: "2.4rem" }} placeholder={tx("recognized.search", "recognized.searchChild", "Search lodges...")} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="lodge-search" />
      </div>

      {filtered.length === 0 ? (
        <p style={{ fontStyle: "italic", color: "#858481" }}>{t("recognized.noResults")}</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((l) => (
            <div key={l.id} className="lodge-card" data-testid={`lodge-${l.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div style={{ fontFamily: "Cinzel, serif", fontSize: "1.1rem", color: "#0d1b2a", letterSpacing: "0.08em" }}>
                    {l.name} {l.number && <span className="kv-label" style={{ marginLeft: 6 }}>{l.number}</span>}
                  </div>
                  {l.obedience && <div style={{ fontStyle: "italic", color: "#9a7530", marginTop: "0.2rem" }}>{l.obedience}</div>}
                  <div className="mt-3 space-y-1" style={{ fontSize: "0.92rem" }}>
                    {(l.city || l.country) && (
                      <div className="flex items-center gap-2"><MapPin size={13} color="#858481" /> <span>{[l.city, l.country].filter(Boolean).join(", ")}</span></div>
                    )}
                    {l.email && <div className="flex items-center gap-2"><Mail size={13} color="#858481" /> <span>{l.email}</span></div>}
                    {l.phone && <div className="flex items-center gap-2"><Phone size={13} color="#858481" /> <span>{l.phone}</span></div>}
                    {l.website && <div className="flex items-center gap-2"><Globe2 size={13} color="#858481" /> <span>{l.website}</span></div>}
                    {/* Grand Lodge officers — render only rows that
                        actually have a value so older entries don't show
                        blank labels. Order mirrors the canonical 11-step
                        Grand-Lodge hierarchy. */}
                    {(l.mare_maestru || l.venerabil_maestru) && (
                      <div className="kv-row" style={{ paddingTop: "0.8rem" }}>
                        <span className="kv-label">{tx("recognized.grandMaster", "lodgeInfo.officerWM", "Worshipful Master")}</span>
                        <span className="kv-value">{l.mare_maestru || l.venerabil_maestru}</span>
                      </div>
                    )}
                    {l.deputy_grand_master && (
                      <div className="kv-row">
                        <span className="kv-label">{tx("recognized.deputyGrandMaster", "lodgeInfo.officerDGM", "Deputy Grand Master")}</span>
                        <span className="kv-value">{l.deputy_grand_master}</span>
                      </div>
                    )}
                    {(l.senior_grand_warden || l.prim_supraveghetor) && (
                      <div className="kv-row">
                        <span className="kv-label">{tx("recognized.seniorGrandWarden", "lodgeInfo.officerSW", "Senior Warden")}</span>
                        <span className="kv-value">{l.senior_grand_warden || l.prim_supraveghetor}</span>
                      </div>
                    )}
                    {l.junior_grand_warden && (
                      <div className="kv-row">
                        <span className="kv-label">{tx("recognized.juniorGrandWarden", "lodgeInfo.officerJW", "Junior Warden")}</span>
                        <span className="kv-value">{l.junior_grand_warden}</span>
                      </div>
                    )}
                    {(l.mare_secretar || l.secretar) && (
                      <div className="kv-row">
                        <span className="kv-label">{tx("recognized.grandSecretary", "lodgeInfo.officerSec", "Secretary")}</span>
                        <span className="kv-value">{l.mare_secretar || l.secretar}</span>
                      </div>
                    )}
                    {l.grand_treasurer && (
                      <div className="kv-row">
                        <span className="kv-label">{tx("recognized.grandTreasurer", "lodgeInfo.officerTres", "Treasurer")}</span>
                        <span className="kv-value">{l.grand_treasurer}</span>
                      </div>
                    )}
                    {l.grand_orator && (
                      <div className="kv-row">
                        <span className="kv-label">{tx("recognized.grandOrator", "lodgeInfo.officerOrator", "Orator")}</span>
                        <span className="kv-value">{l.grand_orator}</span>
                      </div>
                    )}
                    {l.grand_chancellor && (
                      <div className="kv-row">
                        <span className="kv-label">{tx("recognized.grandChancellor", "lodgeInfo.officerChancellor", "Chancellor")}</span>
                        <span className="kv-value">{l.grand_chancellor}</span>
                      </div>
                    )}
                    {l.grand_expert && (
                      <div className="kv-row">
                        <span className="kv-label">{tx("recognized.grandExpert", "lodgeInfo.officerExpert", "Expert")}</span>
                        <span className="kv-value">{l.grand_expert}</span>
                      </div>
                    )}
                    {l.grand_director_of_ceremonies && (
                      <div className="kv-row">
                        <span className="kv-label">{tx("recognized.grandDirCer", "lodgeInfo.officerDirCer", "Director of Ceremonies")}</span>
                        <span className="kv-value">{l.grand_director_of_ceremonies}</span>
                      </div>
                    )}
                    {l.grand_almoner && (
                      <div className="kv-row">
                        <span className="kv-label">{tx("recognized.grandAlmoner", "lodgeInfo.officerAlmoner", "Almoner")}</span>
                        <span className="kv-value">{l.grand_almoner}</span>
                      </div>
                    )}
                    {l.grand_inner_guard && (
                      <div className="kv-row">
                        <span className="kv-label">{tx("recognized.grandInnerGuard", "lodgeInfo.officerInnerGuard", "Inner Guard")}</span>
                        <span className="kv-value">{l.grand_inner_guard}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className={`badge-status ${l.is_private ? "suspended" : "active"}`}>{l.is_private ? t("recognized.privateFlag") : "PUBLIC"}</span>
                    {l.is_system && <span className="badge-status" style={{ background: "#0d1b2a", color: "#e8c97a", border: "1px solid #c9a84c" }}>{t("recognized.systemManaged")}</span>}
                  </div>
                </div>
                {isAdmin && !l.is_system && (
                  <div className="flex flex-col gap-2">
                    <button className="btn-outline-gold" onClick={() => openEdit(l)}><Edit2 size={12} /></button>
                    <button className="btn-danger" onClick={() => remove(l)}><Trash2 size={12} /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="dialog-overlay" onClick={() => setShowForm(false)}>
          <form className="dialog-panel" onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="lodge-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title" style={{ margin: 0 }}>{editing ? tx("recognized.editLodge", "recognized.editLodgeChild", "EDIT LODGE") : tx("recognized.newLodge", "recognized.newLodgeChild", "NEW LODGE")}</h2>
              <button type="button" onClick={() => setShowForm(false)}><X size={20} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="label-lodge">{tx("recognized.lodgeName", "recognized.lodgeNameChild", "Lodge name")} *</label>
                <input className="input-lodge" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="label-lodge">{t("recognized.lodgeNumber")}</label>
                <input className="input-lodge" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} />
              </div>
              <div>
                <label className="label-lodge">{t("recognized.obedience")}</label>
                <input className="input-lodge" value={form.obedience} onChange={(e) => setForm({ ...form, obedience: e.target.value })} />
              </div>
              <div>
                <label className="label-lodge">{t("recognized.city")}</label>
                <input className="input-lodge" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </div>
              <div>
                <label className="label-lodge">{t("recognized.country")}</label>
                <input className="input-lodge" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
              </div>
              <div>
                <label className="label-lodge">{t("recognized.jurisdiction")}</label>
                <input className="input-lodge" value={form.jurisdiction} onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })} />
              </div>
              <div>
                <label className="label-lodge">{t("recognized.founded")}</label>
                <input className="input-lodge" value={form.founded} onChange={(e) => setForm({ ...form, founded: e.target.value })} />
              </div>
              <div>
                <label className="label-lodge">{t("recognized.yearOpened")}</label>
                <input className="input-lodge" value={form.year_opened} onChange={(e) => setForm({ ...form, year_opened: e.target.value })} />
              </div>
              <div>
                <label className="label-lodge">{t("recognized.meeting")}</label>
                <input className="input-lodge" value={form.meeting} onChange={(e) => setForm({ ...form, meeting: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="label-lodge">{t("recognized.address")}</label>
                <input className="input-lodge" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
              <div>
                <label className="label-lodge">{t("common.email")}</label>
                <input className="input-lodge" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className="label-lodge">{t("common.phone")}</label>
                <input className="input-lodge" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="label-lodge">{t("recognized.website")}</label>
                <input className="input-lodge" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
              </div>
              <div>
                <label className="label-lodge">{tx("recognized.grandMaster", "lodgeInfo.officerWM", "Worshipful Master")}</label>
                <input className="input-lodge" value={form.mare_maestru || ""} onChange={(e) => setForm({ ...form, mare_maestru: e.target.value })} data-testid="recognized-grand-master" />
              </div>
              <div>
                <label className="label-lodge">{tx("recognized.deputyGrandMaster", "lodgeInfo.officerDGM", "Deputy Grand Master")}</label>
                <input className="input-lodge" value={form.deputy_grand_master || ""} onChange={(e) => setForm({ ...form, deputy_grand_master: e.target.value })} data-testid="recognized-deputy-grand-master" />
              </div>
              <div>
                <label className="label-lodge">{tx("recognized.seniorGrandWarden", "lodgeInfo.officerSW", "Senior Warden")}</label>
                <input className="input-lodge" value={form.senior_grand_warden || ""} onChange={(e) => setForm({ ...form, senior_grand_warden: e.target.value })} data-testid="recognized-senior-grand-warden" />
              </div>
              <div>
                <label className="label-lodge">{tx("recognized.juniorGrandWarden", "lodgeInfo.officerJW", "Junior Warden")}</label>
                <input className="input-lodge" value={form.junior_grand_warden || ""} onChange={(e) => setForm({ ...form, junior_grand_warden: e.target.value })} data-testid="recognized-junior-grand-warden" />
              </div>
              <div>
                <label className="label-lodge">{tx("recognized.grandSecretary", "lodgeInfo.officerSec", "Secretary")}</label>
                <input className="input-lodge" value={form.mare_secretar || ""} onChange={(e) => setForm({ ...form, mare_secretar: e.target.value })} data-testid="recognized-grand-secretary" />
              </div>
              <div>
                <label className="label-lodge">{tx("recognized.grandTreasurer", "lodgeInfo.officerTres", "Treasurer")}</label>
                <input className="input-lodge" value={form.grand_treasurer || ""} onChange={(e) => setForm({ ...form, grand_treasurer: e.target.value })} data-testid="recognized-grand-treasurer" />
              </div>
              <div>
                <label className="label-lodge">{tx("recognized.grandOrator", "lodgeInfo.officerOrator", "Orator")}</label>
                <input className="input-lodge" value={form.grand_orator || ""} onChange={(e) => setForm({ ...form, grand_orator: e.target.value })} data-testid="recognized-grand-orator" />
              </div>
              <div>
                <label className="label-lodge">{tx("recognized.grandChancellor", "lodgeInfo.officerChancellor", "Chancellor")}</label>
                <input className="input-lodge" value={form.grand_chancellor || ""} onChange={(e) => setForm({ ...form, grand_chancellor: e.target.value })} data-testid="recognized-grand-chancellor" />
              </div>
              <div>
                <label className="label-lodge">{tx("recognized.grandExpert", "lodgeInfo.officerExpert", "Expert")}</label>
                <input className="input-lodge" value={form.grand_expert || ""} onChange={(e) => setForm({ ...form, grand_expert: e.target.value })} data-testid="recognized-grand-expert" />
              </div>
              <div>
                <label className="label-lodge">{tx("recognized.grandDirCer", "lodgeInfo.officerDirCer", "Director of Ceremonies")}</label>
                <input className="input-lodge" value={form.grand_director_of_ceremonies || ""} onChange={(e) => setForm({ ...form, grand_director_of_ceremonies: e.target.value })} data-testid="recognized-grand-dir-ceremonies" />
              </div>
              <div>
                <label className="label-lodge">{tx("recognized.grandAlmoner", "lodgeInfo.officerAlmoner", "Almoner")}</label>
                <input className="input-lodge" value={form.grand_almoner || ""} onChange={(e) => setForm({ ...form, grand_almoner: e.target.value })} data-testid="recognized-grand-almoner" />
              </div>
              <div>
                <label className="label-lodge">{tx("recognized.grandInnerGuard", "lodgeInfo.officerInnerGuard", "Inner Guard")}</label>
                <input className="input-lodge" value={form.grand_inner_guard || ""} onChange={(e) => setForm({ ...form, grand_inner_guard: e.target.value })} data-testid="recognized-grand-inner-guard" />
              </div>
              <div className="md:col-span-2">
                <label className="label-lodge">{t("common.notes")}</label>
                <textarea rows={3} className="input-lodge" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" className="btn-outline-gold" onClick={() => setShowForm(false)}>{t("common.cancel")}</button>
              <button type="submit" className="btn-primary-gold" disabled={saving}>{saving ? "..." : t("common.save")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
